import React, { createContext, useContext, useState, ReactNode } from 'react';
import { CommentType } from '../types/types';

type CommentSource = 'Drawing' | 'Spec' | 'Custom Check' | 'Other';

type VisibleCommentTypesContextType = {
  visibleCommentTypes: CommentType[];
  visibleSources: CommentSource[];
  showExportComments: boolean;
  toggleVisibleCommentType: (type: CommentType) => void;
  toggleVisibleSource: (source: CommentSource) => void;
  toggleExportComments: () => void;
};

const VisibleCommentTypesContext = createContext<VisibleCommentTypesContextType | undefined>(undefined);

type VisibleCommentTypesProviderProps = {
  children: ReactNode;
};

export const VisibleCommentTypesProvider: React.FC<VisibleCommentTypesProviderProps> = ({ children }) => {
  const [visibleCommentTypes, setVisibleCommentTypes] = useState<CommentType[]>(Object.values(CommentType));
  const [visibleSources, setVisibleSources] = useState<CommentSource[]>(['Drawing', 'Spec', 'Custom Check', 'Other']);
  const [showExportComments, setShowExportComments] = useState(true);

  const toggleVisibleCommentType = (type: CommentType) => {
    setVisibleCommentTypes(prevVisibleCommentTypes =>
      prevVisibleCommentTypes.includes(type)
        ? prevVisibleCommentTypes.filter(t => t !== type)
        : [...prevVisibleCommentTypes, type]
    );
  };

  const toggleVisibleSource = (source: CommentSource) => {
    setVisibleSources(prevVisibleSources =>
      prevVisibleSources.includes(source)
        ? prevVisibleSources.filter(s => s !== source)
        : [...prevVisibleSources, source]
    );
  };

  const toggleExportComments = () => {
    setShowExportComments(prev => !prev);
  };

  return (
    <VisibleCommentTypesContext.Provider value={{ 
      visibleCommentTypes, 
      visibleSources,
      showExportComments,
      toggleVisibleCommentType,
      toggleVisibleSource,
      toggleExportComments
    }}>
      {children}
    </VisibleCommentTypesContext.Provider>
  );
};

export const useVisibleCommentTypes = () => {
  const context = useContext(VisibleCommentTypesContext);
  if (!context) {
    throw new Error('useVisibleCommentTypes must be used within a VisibleCommentTypesProvider');
  }
  return context;
};
